import {
  Event,
  Shift,
  WaitlistEntry,
  WaitlistEntryWithEvent,
  WaitlistStatus,
} from '@amzn/red-velvet-api';
import { ShiftStatus } from './volunteerEventShiftsTableTypes';
import { getShiftStatus } from './volunteerEventShiftsTableUtils';

/**
 * One event the user is waitlisted on, with the shifts they hold an ACTIVE waitlist entry for. Drives a
 * single VolunteerEventShiftsTable card on the Waitlists tab. The card's live entries are threaded in
 * separately (see mapActiveWaitlistEntriesByEventId) so they can update while this membership is frozen.
 */
export interface WaitlistEventGroup {
  event: Event;
  shifts: Shift[];
}

/**
 * Group the user's ACTIVE waitlist entries by event. Terminal entries (WITHDRAWN / SIGNED_UP /
 * CANCELLED_AFTER_SIGNUP) are dropped — the tab only shows shifts the user is currently waitlisted on,
 * so an event with no ACTIVE entries produces no card. Insertion order is preserved.
 */
export function groupActiveWaitlistEntriesByEvent(
  entries: WaitlistEntryWithEvent[]
): WaitlistEventGroup[] {
  const groupsByEventId = new Map<string, WaitlistEventGroup>();

  for (const { waitlistEntry, event, shift } of entries) {
    if (waitlistEntry.status !== WaitlistStatus.ACTIVE) continue;

    const existing = groupsByEventId.get(event.eventId);
    if (existing) {
      existing.shifts.push(shift);
    } else {
      groupsByEventId.set(event.eventId, { event, shifts: [shift] });
    }
  }

  return Array.from(groupsByEventId.values());
}

/**
 * The user's live ACTIVE waitlist entries keyed by eventId, so the Waitlists tab can thread the current
 * entries into each (frozen) event card. When the user leaves a waitlist the entry drops out here,
 * flipping the row's Action to "Join waitlist" in place instead of removing the frozen card. Terminal
 * entries (WITHDRAWN / SIGNED_UP / CANCELLED_AFTER_SIGNUP) are excluded.
 */
export function mapActiveWaitlistEntriesByEventId(
  entries: WaitlistEntryWithEvent[]
): Map<string, WaitlistEntry[]> {
  const byEventId = new Map<string, WaitlistEntry[]>();

  for (const { waitlistEntry, event } of entries) {
    if (waitlistEntry.status !== WaitlistStatus.ACTIVE) continue;

    const existing = byEventId.get(event.eventId);
    if (existing) {
      existing.push(waitlistEntry);
    } else {
      byEventId.set(event.eventId, [waitlistEntry]);
    }
  }

  return byEventId;
}

/**
 * An event belongs in the Past section only when ALL of its waitlisted shifts have ended (status PAST).
 * A single upcoming or in-progress waitlisted shift keeps the event in the active section.
 */
export function isPastWaitlistEventGroup(group: WaitlistEventGroup): boolean {
  return group.shifts.every((shift) => getShiftStatus(shift) === ShiftStatus.PAST);
}

/**
 * Partition the user's waitlist entries into active (≥1 not-ended shift) and past (all shifts ended)
 * event groups for the two independently-paginated sections of the Waitlists tab.
 */
export function partitionWaitlistEventGroups(entries: WaitlistEntryWithEvent[]): {
  activeGroups: WaitlistEventGroup[];
  pastGroups: WaitlistEventGroup[];
} {
  const groups = groupActiveWaitlistEntriesByEvent(entries);
  const activeGroups: WaitlistEventGroup[] = [];
  const pastGroups: WaitlistEventGroup[] = [];

  for (const group of groups) {
    (isPastWaitlistEventGroup(group) ? pastGroups : activeGroups).push(group);
  }

  return { activeGroups, pastGroups };
}

/**
 * Format an event's date for the card header: a single day, or a "start – end" range when the event
 * spans multiple days.
 */
export function formatEventDateRange(event: Event, locale: string): string {
  if (!event.startTimestamp) return '';

  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  const start = new Date(event.startTimestamp).toLocaleDateString(locale, options);

  if (event.endTimestamp) {
    const end = new Date(event.endTimestamp).toLocaleDateString(locale, options);
    if (end !== start) return `${start} – ${end}`;
  }

  return start;
}
